"use client";

import * as React from "react";
import { useState } from "react";
import { Mail, MessageSquare, Plus, Minus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
const useWvcLanguage = () => {
  const [language, setLanguage] = React.useState<"en" | "zh" | "fr" | "es" | "de">("en");
  React.useEffect(() => {
    const updateLanguage = () => {
      const htmlLang = document.documentElement.lang;
      if (htmlLang && ["en", "zh", "fr", "es", "de"].includes(htmlLang)) {
        setLanguage(htmlLang as "en" | "zh" | "fr" | "es" | "de");
        return;
      }
      const match = document.cookie.match(/wvc_lang=([^;]+)/);
      if (match && ["en", "zh", "fr", "es", "de"].includes(match[1])) {
        setLanguage(match[1] as "en" | "zh" | "fr" | "es" | "de");
        return;
      }
      try {
        const stored = localStorage.getItem("wvc_lang");
        if (stored && ["en", "zh", "fr", "es", "de"].includes(stored)) {
          setLanguage(stored as "en" | "zh" | "fr" | "es" | "de");
        }
      } catch (_) {}
    };
    updateLanguage();
    const handleLangChange = (e: any) => {
      if (e.detail && ["en", "zh", "fr", "es", "de"].includes(e.detail)) {
        setLanguage(e.detail);
      } else {
        updateLanguage();
      }
    };
    window.addEventListener("wvc-language-changed", handleLangChange);
    return () => window.removeEventListener("wvc-language-changed", handleLangChange);
  }, []);
  return language;
};
interface FaqItem {
  id: string;
  category: string;
  question: string;
  answer: string;
}
const translations = {
  en: {
    assistance: "Client Assistance",
    frequently: "Frequently asked",
    questions: "questions",
    description: "Find straightforward details on orders, international delivery, sizing, and garment preservation.",
    ordersDelivery: "Orders & delivery",
    fitSizing: "Fit & sizing",
    returnsRefunds: "Returns & refunds",
    garmentCare: "Garment care",
    order1Q: "How long does standard domestic delivery take?",
    order1A: "Standard domestic orders ship within 1-2 business days and typically arrive within 3-5 business days, depending on your location. We provide tracking for all shipments.",
    order2Q: "Do you offer international shipping?",
    order2A: "Yes, we ship worldwide via premium courier partners. International orders typically arrive within 7-12 business days and come with full tracking. Custom duties may apply depending on your country.",
    order3Q: "What is the return policy?",
    order3A: "We offer a 30-day return window from delivery. Items must be unwashed, unworn, and have all original tags. Prepaid return labels are included in your order. Refunds are processed within 5 business days.",
    fit1Q: "How do I know if the fit is right for me?",
    fit1A: "Review our detailed size guide and garment measurements. If you're between sizes, we recommend sizing down for structured pieces or up for relaxed, flowing designs. Our styling team can also provide personalized recommendations.",
    fit2Q: "Do you offer custom sizing or alterations?",
    fit2A: "Yes, our concierge team assists with bespoke sizing, specific length requests, and hem adjustments. Contact our styling advisors for a consultation.",
    fit3Q: "What fabrics do you use and how do they fit?",
    fit3A: "We work with premium European natural fibers including pure mulberry silk, structured linens, and fine wools. Silks and poplins have zero stretch, while knitwear naturally adapts to your body. Check each product's care guide for fabric-specific details.",
    return1Q: "What is your returns process?",
    return1A: "Our returns are simple. Pack your unwashed, unworn items with all original tags in the provided prepaid return box. Print the included return label and drop off at any carrier location. Refunds are processed within 5 business days of receipt.",
    return2Q: "Can I exchange items instead of returning them?",
    return2A: "Yes! We offer complimentary size exchanges with instant reservation. Simply contact our concierge team, request your new size, and we'll prepay your return shipping and send your replacement promptly.",
    return3Q: "Are there any items that cannot be returned?",
    return3A: "All items are returnable within 30 days if unwashed, unworn, and in original condition with tags. Items that have been worn, altered, or damaged cannot be accepted for return.",
    care1Q: "How should I care for my garments?",
    care1A: "Each garment includes a detailed care tag with specific instructions. Generally, we recommend dry cleaning or gentle hand washing in cool water for silks and structured pieces, and machine washing on delicate cycles for knitwear. Air drying is always preferred over tumble drying.",
    care2Q: "What is the best way to store my pieces?",
    care2A: "Store garments in a cool, dry place away from direct sunlight. Use wooden hangers for structured pieces to maintain their shape. For knitwear, fold and store on shelves to prevent stretching. Use cedar blocks or lavender sachets for natural moth prevention.",
    care3Q: "How do I refresh my garments between washes?",
    care3A: "Hang garments in a well-ventilated area for a few hours to air out. A light steaming can remove wrinkles from silks and structured pieces without damaging fabrics. Spot clean any marks with a damp cloth. Our pieces are designed for longevity, so frequent washing is not necessary."
  },
  zh: {
    assistance: "客户协助",
    frequently: "经常问",
    questions: "问题",
    description: "查找有关订单、国际交付、尺码和服装保存的简明详细信息。",
    ordersDelivery: "订单和交付",
    fitSizing: "合身和尺码",
    returnsRefunds: "退货和退款",
    garmentCare: "服装护理",
    order1Q: "标准国内交付需要多长时间？",
    order1A: "标准国内订单在1-2个工作日内发货，通常在3-5个工作日内到达，具体取决于您的位置。我们为所有货件提供跟踪。",
    order2Q: "您提供国际运输吗？",
    order2A: "是的，我们通过高级快递合作伙伴向全球发货。国际订单通常在7-12个工作日内到达，并附带完整跟踪。根据您的国家，可能需要支付关税。",
    order3Q: "退货政策是什么？",
    order3A: "我们提供从交付日期起30天的退货窗口。物品必须未洗过、未穿过，并具有所有原始标签。您的订单中包括预付回程标签。退款在5个工作日内处理。",
    fit1Q: "我如何知道合身是否适合我？",
    fit1A: "查看我们详细的尺码指南和服装尺寸。如果您处于两个尺码之间，我们建议对于结构化件向下调整尺码，对于轻松、流动的设计向上调整。我们的造型团队也可以提供个性化的建议。",
    fit2Q: "您提供自定义尺码或修改吗？",
    fit2A: "是的，我们的礼宾团队协助定制尺码、特定长度要求和下摆调整。联系我们的造型顾问进行咨询。",
    fit3Q: "您使用什么织物，它们如何适应？",
    fit3A: "我们使用高级欧洲天然纤维，包括纯桑蚕丝、结构亚麻和精细羊毛。丝绸和棉布没有拉伸，而针织品自然适应您的身体。查看每种产品的护理指南以了解特定织物的详细信息。",
    return1Q: "您的退货流程是什么？",
    return1A: "我们的退货很简单。将未洗过、未穿过的物品以及所有原始标签放在提供的预付回程箱中。打印随附的回程标签，并在任何运输公司位置退货。退款在收到后的5个工作日内处理。",
    return2Q: "我可以交换物品而不是退货吗？",
    return2A: "是的！我们提供免费尺码交换和即时预订。只需联系我们的礼宾团队，请求您的新尺码，我们将预付您的回程运费并及时发送您的替换品。",
    return3Q: "有没有不能退货的物品？",
    return3A: "如果未洗过、未穿过且处于原始状态且带标签，所有物品都可在30天内退货。已穿过、改动过或损坏的物品不接受退货。",
    care1Q: "我应该如何照顾我的服装？",
    care1A: "每件服装都附有详细的护理标签和特定说明。通常，我们建议对丝绸和结构件进行干洗或温水轻轻手洗，对针织品进行温柔的机洗。风干总是比烘干机干燥更好。",
    care2Q: "存储我的件的最佳方式是什么？",
    care2A: "将服装存放在凉爽、干燥的地方，远离阳光直射。对于结构件，使用木制衣架来保持其形状。对于针织品，折叠并存放在架子上以防止拉伸。使用雪松木块或薰衣草香囊进行天然防蛀。",
    care3Q: "我应该如何在洗涤之间刷新我的服装？",
    care3A: "将服装悬挂在通风良好的地方几小时以通风。轻微的蒸汽可以消除丝绸和结构件的皱纹，而不会损坏织物。用湿布清洁任何污迹。我们的件是为了长期使用而设计的，所以不需要经常洗涤。"
  },
  fr: {
    assistance: "Assistance Client",
    frequently: "Fréquemment demandé",
    questions: "questions",
    description: "Trouvez des détails directs sur les commandes, la livraison internationale, les tailles et la préservation des vêtements.",
    ordersDelivery: "Commandes et Livraison",
    fitSizing: "Ajustement et Dimensionnement",
    returnsRefunds: "Retours et Remboursements",
    garmentCare: "Soin des Vêtements",
    order1Q: "Combien de temps prend la livraison standard nationale?",
    order1A: "Les commandes standard nationales sont expédiées dans les 1-2 jours ouvrables et arrivent généralement dans les 3-5 jours ouvrables, selon votre localisation. Nous fournissons un suivi pour tous les envois.",
    order2Q: "Livrez-vous à l'international?",
    order2A: "Oui, nous livrons dans le monde entier via des partenaires de courrier haut de gamme. Les commandes internationales arrivent généralement dans les 7-12 jours ouvrables et incluent un suivi complet. Les droits de douane peuvent s'appliquer selon votre pays.",
    order3Q: "Quelle est la politique de retour?",
    order3A: "Nous offrons une fenêtre de retour de 30 jours à partir de la livraison. Les articles doivent être non lavés, non portés et avoir toutes les étiquettes d'origine. Des étiquettes de retour prépayées sont incluses dans votre commande. Les remboursements sont traités dans les 5 jours ouvrables.",
    fit1Q: "Comment savoir si l'ajustement me convient?",
    fit1A: "Consultez notre guide des tailles détaillé et les mesures des vêtements. Si vous hésitez entre deux tailles, nous vous recommandons de réduire la taille pour les pièces structurées ou d'augmenter pour les designs détendus et fluides. Notre équipe de style peut également fournir des recommandations personnalisées.",
    fit2Q: "Proposez-vous des tailles personnalisées ou des modifications?",
    fit2A: "Oui, notre équipe de concierge vous aide avec le dimensionnement personnalisé, les demandes de longueur spécifiques et les modifications d'ourlet. Contactez nos conseillers en style pour une consultation.",
    fit3Q: "Quels tissus utilisez-vous et comment s'ajustent-ils?",
    fit3A: "Nous travaillons avec des fibres naturelles européennes haut de gamme, notamment la soie de mûrier pur, les lins structurés et les laines fines. Les soies et les poplins n'ont aucun étirement, tandis que les tricots s'adaptent naturellement à votre corps. Vérifiez le guide de soin de chaque produit pour des détails spécifiques au tissu.",
    return1Q: "Quel est votre processus de retour?",
    return1A: "Nos retours sont simples. Emballez vos articles non lavés, non portés avec toutes les étiquettes d'origine dans la boîte de retour prépayée fournie. Imprimez l'étiquette de retour incluse et déposez-la à n'importe quel endroit de transporteur. Les remboursements sont traités dans les 5 jours ouvrables suivant la réception.",
    return2Q: "Puis-je échanger des articles au lieu de les retourner?",
    return2A: "Oui! Nous offrons des échanges de taille gratuits avec réservation instantanée. Contactez simplement notre équipe de concierge, demandez votre nouvelle taille, et nous prépayerons vos frais de retour et enverrons votre remplacement rapidement.",
    return3Q: "Y a-t-il des articles qui ne peuvent pas être retournés?",
    return3A: "Tous les articles sont retournables dans les 30 jours s'ils n'ont pas été lavés, portés et sont en état d'origine avec les étiquettes. Les articles qui ont été portés, modifiés ou endommagés ne peuvent pas être acceptés pour retour.",
    care1Q: "Comment dois-je entretenir mes vêtements?",
    care1A: "Chaque vêtement comprend une étiquette de soin détaillée avec des instructions spécifiques. En général, nous recommandons le nettoyage à sec ou le lavage à la main délicat à l'eau froide pour les soies et les pièces structurées, et le lavage en machine sur cycles délicat pour les tricots. Le séchage à l'air est toujours préférable au séchage à la machine.",
    care2Q: "Quel est le meilleur moyen de stocker mes pièces?",
    care2A: "Rangez les vêtements dans un endroit frais et sec, à l'abri de la lumière directe du soleil. Utilisez des cintres en bois pour les pièces structurées afin de préserver leur forme. Pour les tricots, pliez et rangez sur les étagères pour éviter l'étirement. Utilisez des blocs de cèdre ou des sachets de lavande pour la prévention naturelle des mites.",
    care3Q: "Comment rafraîchir mes vêtements entre les lavages?",
    care3A: "Accrochez les vêtements dans une zone bien ventilée pendant quelques heures pour les aérer. Un léger vapeur peut éliminer les plis des soies et des pièces structurées sans endommager les tissus. Nettoyez délicatement tout marque avec un chiffon humide. Nos pièces sont conçues pour la longévité, donc un lavage fréquent n'est pas nécessaire."
  },
  es: {
    assistance: "Asistencia del Cliente",
    frequently: "Preguntado Frecuentemente",
    questions: "preguntas",
    description: "Encuentre detalles directos sobre pedidos, entrega internacional, tamaños y conservación de prendas.",
    ordersDelivery: "Pedidos y Entrega",
    fitSizing: "Ajuste y Tamaño",
    returnsRefunds: "Devoluciones y Reembolsos",
    garmentCare: "Cuidado de Prendas",
    order1Q: "¿Cuánto tiempo tarda la entrega estándar nacional?",
    order1A: "Los pedidos estándar nacionales se envían dentro de 1-2 días hábiles y generalmente llegan dentro de 3-5 días hábiles, dependiendo de su ubicación. Proporcionamos seguimiento para todos los envíos.",
    order2Q: "¿Ofrecen envíos internacionales?",
    order2A: "Sí, enviamos a todo el mundo a través de socios de mensajería premium. Los pedidos internacionales generalmente llegan dentro de 7-12 días hábiles y vienen con seguimiento completo. Pueden aplicarse derechos aduanales según su país.",
    order3Q: "¿Cuál es la política de devolución?",
    order3A: "Ofrecemos una ventana de devolución de 30 días a partir de la entrega. Los artículos deben estar sin lavar, sin usar y tener todas las etiquetas originales. Las etiquetas de devolución prepagadas se incluyen en su pedido. Los reembolsos se procesan dentro de 5 días hábiles.",
    fit1Q: "¿Cómo sé si el ajuste es adecuado para mí?",
    fit1A: "Revise nuestra guía de tallas detallada y las medidas de las prendas. Si estás entre tallas, te recomendamos reducir el tamaño para piezas estructuradas o aumentar para diseños relajados y fluidos. Nuestro equipo de estilo también puede proporcionar recomendaciones personalizadas.",
    fit2Q: "¿Ofrecen tamaños personalizados o alteraciones?",
    fit2A: "Sí, nuestro equipo de conserjería lo ayuda con dimensionamiento personalizado, solicitudes de longitud específicas y alteraciones de dobladillo. Póngase en contacto con nuestros asesores de estilo para una consulta.",
    fit3Q: "¿Qué telas utilizas y cómo se ajustan?",
    fit3A: "Trabajamos con fibras naturales europeas premium, incluida seda de morera pura, linos estructurados y lanas finas. Las sedas y poplinas no tienen elasticidad, mientras que la ropa de punto se adapta naturalmente a tu cuerpo. Consulta la guía de cuidado de cada producto para obtener detalles específicos de la tela.",
    return1Q: "¿Cuál es tu proceso de devolución?",
    return1A: "Nuestras devoluciones son simples. Empaque artículos sin lavar, sin usar con todas las etiquetas originales en la caja de devolución prepagada proporcionada. Imprima la etiqueta de devolución incluida y entréguela en cualquier ubicación de transportista. Los reembolsos se procesan dentro de 5 días hábiles de la recepción.",
    return2Q: "¿Puedo intercambiar artículos en lugar de devolverlos?",
    return2A: "¡Sí! Ofrecemos intercambios de talla gratuitos con reserva instantánea. Simplemente póngase en contacto con nuestro equipo de conserjería, solicite su nuevo tamaño y nosotros prepagamos el envío de devolución y le enviamos su reemplazo rápidamente.",
    return3Q: "¿Hay artículos que no se pueden devolver?",
    return3A: "Todos los artículos se pueden devolver dentro de 30 días si no se han lavado, usado y están en condición original con etiquetas. Los artículos que han sido usados, alterados o dañados no se pueden aceptar para devolución.",
    care1Q: "¿Cómo debo cuidar mis prendas?",
    care1A: "Cada prenda incluye una etiqueta de cuidado detallada con instrucciones específicas. Por lo general, recomendamos limpieza en seco o lavado suave a mano en agua fría para sedas y piezas estructuradas, y lavado a máquina en ciclos delicados para prendas de punto. El secado al aire siempre es preferible al secado en secadora.",
    care2Q: "¿Cuál es la mejor manera de almacenar mis piezas?",
    care2A: "Guarde las prendas en un lugar fresco y seco, lejos de la luz solar directa. Utilice perchas de madera para piezas estructuradas para mantener su forma. Para prendas de punto, dobla y guarda en estantes para evitar el estiramiento. Usa bloques de cedro o bolsitas de lavanda para la prevención natural de polillas.",
    care3Q: "¿Cómo actualizo mis prendas entre lavados?",
    care3A: "Cuelga las prendas en un área bien ventilada durante unas horas para airearlas. Un ligero vapor puede eliminar las arrugas de sedas y piezas estructuradas sin dañar las telas. Limpia cuidadosamente cualquier mancha con un paño húmedo. Nuestras piezas están diseñadas para la longevidad, por lo que el lavado frecuente no es necesario."
  },
  de: {
    assistance: "Kundenunterstützung",
    frequently: "Häufig gefragt",
    questions: "Fragen",
    description: "Finden Sie unkomplizierte Details zu Bestellungen, internationaler Lieferung, Größenbestimmung und Kleidungskonservierung.",
    ordersDelivery: "Bestellungen und Lieferung",
    fitSizing: "Anpassung und Größe",
    returnsRefunds: "Rückgaben und Rückerstattungen",
    garmentCare: "Kleidungspflege",
    order1Q: "Wie lange dauert die Standard-Inlandslieferung?",
    order1A: "Standard-Inlandsbestellungen werden innerhalb von 1-2 Arbeitstagen versandt und kommen normalerweise innerhalb von 3-5 Arbeitstagen an, je nach Ihrem Standort. Wir bieten Verfolgung für alle Sendungen.",
    order2Q: "Bieten Sie internationalen Versand an?",
    order2A: "Ja, wir versenden weltweit über Premium-Kurierdienste. Internationale Bestellungen kommen normalerweise innerhalb von 7-12 Arbeitstagen an und sind vollständig verfolgbar. Je nach Land können Zollgebühren anfallen.",
    order3Q: "Was ist die Rückgabefrist?",
    order3A: "Wir bieten ein 30-tägiges Rückgabefenster ab Lieferung. Artikel müssen ungewaschen, ungetragen und mit allen Original-Tags versehen sein. Bezahlte Rücksendeetiketten sind in Ihrer Bestellung enthalten. Rückerstattungen werden innerhalb von 5 Arbeitstagen verarbeitet.",
    fit1Q: "Woher weiß ich, ob die Passform für mich richtig ist?",
    fit1A: "Beachten Sie unseren detaillierten Größenleitfaden und die Kleidungsmessungen. Wenn Sie zwischen zwei Größen liegen, empfehlen wir Ihnen, die Größe für strukturierte Teile zu verringern oder für lockere, fließende Designs zu vergrößern. Unser Styling-Team kann auch personalisierte Empfehlungen geben.",
    fit2Q: "Bieten Sie benutzerdefinierte Größen oder Änderungen an?",
    fit2A: "Ja, unser Concierge-Team hilft bei maßgeschneiderten Größen, spezifischen Längenwünschen und Saumanpassungen. Wenden Sie sich an unsere Styling-Berater für eine Beratung.",
    fit3Q: "Welche Stoffe verwenden Sie und wie passen sie?",
    fit3A: "Wir arbeiten mit hochwertigen europäischen Naturfasern, einschließlich reiner Maulbeerseiden, strukturierten Leinen und feinen Wolle. Seide und Popeline haben keinen Dehnungsspielraum, während Strickwaren sich natürlich an Ihren Körper anpassen. Beachten Sie die Pflegeanleitung jedes Produkts für stoffspezifische Details.",
    return1Q: "Was ist Ihr Rückgabeverfahren?",
    return1A: "Unsere Rückgaben sind einfach. Verpacken Sie Ihre ungewaschenen, ungenutzten Artikel mit allen Original-Tags in der bereitgestellten bezahlten Rückgabebox. Drucken Sie das beiliegende Rücksendeetikett aus und geben Sie es an beliebigen Transportortstellen ab. Rückerstattungen werden innerhalb von 5 Arbeitstagen nach Erhalt verarbeitet.",
    return2Q: "Kann ich Artikel austauschen, anstatt sie zurückzugeben?",
    return2A: "Ja! Wir bieten kostenlose Größentausch mit sofortiger Reservierung. Wenden Sie sich einfach an unser Concierge-Team, fordern Sie Ihre neue Größe an, und wir bezahlen Ihre Rücksendekosten vor und senden Ihren Ersatz schnell.",
    return3Q: "Gibt es Artikel, die nicht zurückgegeben werden können?",
    return3A: "Alle Artikel können innerhalb von 30 Tagen zurückgegeben werden, wenn sie ungewaschen, ungetragen und in Originalzustand mit Tags sind. Artikel, die getragen, verändert oder beschädigt wurden, können nicht zur Rückgabe angenommen werden.",
    care1Q: "Wie sollte ich meine Kleidung pflegen?",
    care1A: "Jedes Kleidungsstück hat ein detailliertes Pflegeetikett mit spezifischen Anweisungen. Im Allgemeinen empfehlen wir Trockenreinigung oder sanfte Handwäsche in kaltem Wasser für Seide und strukturierte Teile sowie Maschinenwäsche im Schonwaschgang für Strickwaren. Lufttrocknung ist dem Wäschetrockner immer vorzuziehen.",
    care2Q: "Was ist der beste Weg, um meine Teile zu lagern?",
    care2A: "Lagern Sie Kleidung an einem kühlen, trockenen Ort, fern von direkter Sonneneinstrahlung. Verwenden Sie Holzbügel für strukturierte Teile, um ihre Form zu erhalten. Bei Strickwaren falten und auf Regalen lagern, um Dehnung zu verhindern. Verwenden Sie Zedernblöcke oder Lavendelkissen zur natürlichen Mottenbekämpfung.",
    care3Q: "Wie erfrische ich meine Kleidung zwischen den Wäschen?",
    care3A: "Hängen Sie Kleidung für ein paar Stunden an einer gut belüfteten Stelle auf, um sie zu lüften. Ein leichtes Dampfen kann Falten aus Seide und strukturierten Teilen entfernen, ohne Stoffe zu beschädigen. Reinigen Sie alle Flecken mit einem feuchten Tuch. Unsere Teile sind für Langlebigkeit konzipiert, daher ist häufiges Waschen nicht erforderlich."
  }
};
export default function Faq() {
  const language = useWvcLanguage();
  const t = translations[language] || translations.en;
  const [activeTab, setActiveTab] = useState("orders");
  const [openItem, setOpenItem] = useState<string | undefined>("orders-1");
  const categories = [{
    id: "orders",
    label: t.ordersDelivery
  }, {
    id: "sizing",
    label: t.fitSizing
  }, {
    id: "returns",
    label: t.returnsRefunds
  }, {
    id: "care",
    label: t.garmentCare
  }];
  const faqData: FaqItem[] = [{
    id: "orders-1",
    category: "orders",
    question: t.order1Q,
    answer: t.order1A
  }, {
    id: "orders-2",
    category: "orders",
    question: t.order2Q,
    answer: t.order2A
  }, {
    id: "orders-3",
    category: "orders",
    question: t.order3Q,
    answer: t.order3A
  }, {
    id: "sizing-1",
    category: "sizing",
    question: t.fit1Q,
    answer: t.fit1A
  }, {
    id: "sizing-2",
    category: "sizing",
    question: t.fit2Q,
    answer: t.fit2A
  }, {
    id: "sizing-3",
    category: "sizing",
    question: t.fit3Q,
    answer: t.fit3A
  }, {
    id: "returns-1",
    category: "returns",
    question: t.return1Q,
    answer: t.return1A
  }, {
    id: "returns-2",
    category: "returns",
    question: t.return2Q,
    answer: t.return2A
  }, {
    id: "returns-3",
    category: "returns",
    question: t.return3Q,
    answer: t.return3A
  }, {
    id: "care-1",
    category: "care",
    question: t.care1Q,
    answer: t.care1A
  }, {
    id: "care-2",
    category: "care",
    question: t.care2Q,
    answer: t.care2A
  }, {
    id: "care-3",
    category: "care",
    question: t.care3Q,
    answer: t.care3A
  }];
  return <section id="常见问题" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-5xl">
        {/* Header Section */}
        <div className="text-center mb-12 md:mb-16">
          <p className="font-sans text-xs uppercase tracking-widest text-muted-foreground mb-3 font-semibold">
            {t.assistance}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-4">
            {t.frequently} <span className="italic font-serif">{t.questions}</span>
          </h2>
          <p className="font-sans text-muted-foreground text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            {t.description}
          </p>
        </div>

        {/* Category Tabs & Accordion */}
        <Tabs value={activeTab} onValueChange={val => {
        setActiveTab(val);
        const firstItem = faqData.find(item => item.category === val);
        if (firstItem) {
          setOpenItem(firstItem.id);
        }
      }} className="w-full space-y-8">
          {/* Scrollable / Responsive Tab List */}
          <div className="flex justify-center border-b border-border pb-px overflow-x-auto no-scrollbar">
            <TabsList className="bg-transparent h-auto p-0 gap-2 sm:gap-6 rounded-none flex-nowrap">
              {categories.map((cat, i) => <div key={cat.id} data-index={i}>
                  <TabsTrigger value={cat.id} className="font-sans text-xs sm:text-sm tracking-wide uppercase px-3 sm:px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground hover:text-foreground transition-all shadow-none">
                    {cat.label}
                  </TabsTrigger>
                </div>)}
            </TabsList>
          </div>

          {/* Tab Contents */}
          {categories.map((cat, catIndex) => {
          const items = faqData.filter(item => item.category === cat.id);
          return <div key={cat.id} data-index={catIndex}>
                <TabsContent value={cat.id} className="mt-4 focus-visible:outline-none">
                  <Accordion type="single" collapsible value={openItem} onValueChange={val => setOpenItem(val)} className="w-full divide-y divide-border border-t border-b border-border">
                    {items.map((item, itemIndex) => {
                  const isOpen = openItem === item.id;
                  return <div key={item.id} data-index={itemIndex}>
                          <AccordionItem value={item.id} className="border-none py-1 group transition-colors hover:bg-card/40">
                            <AccordionTrigger className="py-5 px-2 text-left font-sans text-base sm:text-lg font-medium text-foreground hover:no-underline flex justify-between items-center gap-4 transition-colors group-hover:text-primary [&>svg]:hidden">
                              <span className="pr-4 leading-snug">{item.question}</span>
                              <span className="shrink-0 flex items-center justify-center size-7 border border-border bg-background text-foreground transition-all group-hover:border-foreground">
                                {isOpen ? <Minus className="size-3.5 stroke-[1.5]" /> : <Plus className="size-3.5 stroke-[1.5]" />}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="px-2 pb-6 pt-1 text-sm sm:text-base font-sans text-muted-foreground leading-relaxed">
                              {item.answer}
                            </AccordionContent>
                          </AccordionItem>
                        </div>;
                })}
                  </Accordion>
                </TabsContent>
              </div>;
        })}
        </Tabs>

        {/* Supplementary Assistance Bar */}
        <div className="mt-14 sm:mt-16 bg-card border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h3 className="font-serif text-lg sm:text-xl font-normal text-foreground">
              Still need assistance?
            </h3>
            <p className="font-sans text-xs sm:text-sm text-muted-foreground">
              Our styling and concierge team is available Monday to Friday, 9am to 6pm GMT.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button variant="outline" size="sm" className="rounded-none border-border hover:bg-background text-foreground text-xs uppercase tracking-wider font-medium px-4 py-2" asChild>
              <a href="mailto:contact@yishengfushi.com">
                <Mail className="size-3.5 mr-2" />
                Email concierge
              </a>
            </Button>
            <Button variant="default" size="sm" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-wider font-medium px-4 py-2 shadow-none" asChild>
              <Link to="/CustomerservicePage">
                <MessageSquare className="size-3.5 mr-2" />
                Customer desk
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}